import React, { useEffect, useState } from "react";
import axios from "axios";
import { Sparkles, TrendingUp } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const fmtCost = (v) => {
  if (v === null || v === undefined) return "—";
  if (v < 0.01) return `<$0.01`;
  return `$${v.toFixed(2)}`;
};

const fmtNum = (v) => {
  if (typeof v !== "number") return "—";
  if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
  return String(v);
};

export const UsageWidget = ({ canSeeOrg = false, onClose }) => {
  const [me, setMe] = useState(null);
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState("me");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [meResp, orgResp] = await Promise.allSettled([
          axios.get(`${API}/me/usage/me`),
          canSeeOrg ? axios.get(`${API}/me/usage/org`) : Promise.reject(),
        ]);
        if (cancelled) return;
        if (meResp.status === "fulfilled") setMe(meResp.value.data);
        if (orgResp.status === "fulfilled") setOrg(orgResp.value.data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [canSeeOrg]);

  const current = scope === "org" ? org : me;

  if (loading) {
    return (
      <div className="px-4 py-3 text-xs text-gray-500" data-testid="usage-widget-loading">
        Cargando consumo del mes…
      </div>
    );
  }

  if (!current) {
    return (
      <div className="px-4 py-3 text-xs text-gray-500" data-testid="usage-widget-empty">
        Sin datos de consumo este mes.
      </div>
    );
  }

  return (
    <div className="px-4 py-3" data-testid="usage-widget">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-purple-400" />
          <span className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">
            Consumo IA · {current.period}
          </span>
        </div>
        {canSeeOrg && org && (
          <div className="flex items-center gap-1 rounded-full bg-white/5 p-0.5 text-[10px]">
            <button
              type="button"
              onClick={() => setScope("me")}
              className={`px-2 py-0.5 rounded-full transition-colors ${scope === "me" ? "bg-purple-500/30 text-white" : "text-gray-400 hover:text-white"}`}
              data-testid="usage-widget-scope-me"
            >
              Yo
            </button>
            <button
              type="button"
              onClick={() => setScope("org")}
              className={`px-2 py-0.5 rounded-full transition-colors ${scope === "org" ? "bg-purple-500/30 text-white" : "text-gray-400 hover:text-white"}`}
              data-testid="usage-widget-scope-org"
            >
              Org
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="rounded-lg bg-purple-500/10 border border-purple-500/20 p-2.5">
          <div className="text-[10px] uppercase tracking-wider text-purple-300/80">Costo</div>
          <div className="text-lg font-bold text-white font-mono mt-0.5" data-testid="usage-widget-cost">
            {fmtCost(current.total_cost_usd)}
          </div>
        </div>
        <div className="rounded-lg bg-white/[0.04] border border-white/[0.08] p-2.5">
          <div className="text-[10px] uppercase tracking-wider text-gray-400">Llamadas</div>
          <div className="text-lg font-bold text-white font-mono mt-0.5" data-testid="usage-widget-calls">
            {fmtNum(current.total_calls)}
          </div>
        </div>
      </div>

      {current.by_tool && current.by_tool.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-gray-500 mb-1.5">
            <TrendingUp className="w-3 h-3" />
            Top herramientas
          </div>
          <div className="space-y-1">
            {current.by_tool.slice(0, 4).map((t) => (
              <div key={t.tool} className="flex items-center justify-between text-[11px]" data-testid={`usage-widget-tool-${t.tool}`}>
                <span className="text-gray-300 truncate max-w-[140px]" title={t.tool}>{t.tool}</span>
                <span className="text-gray-400 font-mono">{fmtCost(t.cost_usd)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="mt-3 text-[10px] leading-tight text-gray-500">
        Estimado en USD basado en tokens consumidos del modelo. Incluye redaccion automatica de PII.
      </p>
    </div>
  );
};
